import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CompanyFilter } from '../components/CompanyFilter';
import { Download, FileText, FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const REPORTS = [
  { id: 'executive-pdf', label: 'Executive Summary (PDF)', desc: 'Boardroom-ready PDF with KPIs, savings, product rankings, site performance, and data quality', icon: FileText, format: 'pdf' },
  { id: 'spend', label: 'Spend Analysis', desc: 'Spend breakdown by supplier, product, and monthly trend', icon: FileSpreadsheet, format: 'excel' },
  { id: 'product-comparison', label: 'Tooth Point Comparison', desc: 'Products ranked by long-run cost per operating hour with confidence levels', icon: FileSpreadsheet, format: 'excel' },
  { id: 'savings', label: 'Savings Opportunities', desc: 'Product switch recommendations and projected savings', icon: FileSpreadsheet, format: 'excel' },
  { id: 'supplier-comparison', label: 'Supplier Comparison', desc: 'Supplier spend, performance, and reliability metrics', icon: FileSpreadsheet, format: 'excel' },
  { id: 'site-summary', label: 'Site Performance Summary', desc: 'Per-site spend, cost/hr, machine count, and efficiency', icon: FileSpreadsheet, format: 'excel' },
  { id: 'machine-summary', label: 'Machine Cost Summary', desc: 'Per-machine spend, change-out frequency, and product mix', icon: FileSpreadsheet, format: 'excel' },
  { id: 'strata-comparison', label: 'Strata & Type Analysis', desc: 'Ground conditions impact and casting vs forging economics', icon: FileSpreadsheet, format: 'excel' },
];

export default function ReportsPage() {
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterSite, setFilterSite] = useState('all');
  const [generating, setGenerating] = useState('');
  const [execSummary, setExecSummary] = useState('');
  const [loadingSummary, setLoadingSummary] = useState(false);

  useEffect(() => {
    api.get('/companies').then(r => setCompanies(r.data)).catch(() => {});
    api.get('/sites').then(r => setSites(r.data)).catch(() => {});
  }, []);

  const downloadReport = async (reportId) => {
    setGenerating(reportId);
    try {
      const p = {};
      if (filterCompany !== 'all') p.company_id = filterCompany;
      if (filterSite !== 'all') p.site_id = filterSite;
      const res = await api.get(`/reports/generate/${reportId}?${new URLSearchParams(p)}`, { responseType: 'blob' });
      const ext = reportId.includes('pdf') ? 'pdf' : 'xlsx';
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `${reportId}_report.${ext}`; a.click();
      toast.success('Report downloaded');
    } catch { toast.error('Report generation failed'); }
    finally { setGenerating(''); }
  };

  const generateExecSummary = async () => {
    setLoadingSummary(true);
    try {
      const p = {};
      if (filterCompany !== 'all') p.company_id = filterCompany;
      if (filterSite !== 'all') p.site_id = filterSite;
      const res = await api.get(`/analytics/executive-summary?${new URLSearchParams(p)}`);
      setExecSummary(res.data.narrative);
    } catch { toast.error('Failed to generate summary'); }
    finally { setLoadingSummary(false); }
  };

  return (
    <div data-testid="reports-page">
      <div className="page-header">
        <div><h1 className="page-title">Reports & Export</h1><p className="text-sm text-slate-500 mt-1">Generate presentation-grade reports for procurement, management, and stakeholders</p></div>
      </div>

      <div className="flex gap-3 mb-6 flex-wrap items-end" data-testid="report-filters">
        <CompanyFilter companies={companies} value={filterCompany} onChange={setFilterCompany} />
        <Select value={filterSite} onValueChange={setFilterSite}><SelectTrigger className="w-48 bg-white"><SelectValue placeholder="All Sites" /></SelectTrigger><SelectContent><SelectItem value="all">All Sites</SelectItem>{sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}</SelectContent></Select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="report-cards">
        {REPORTS.map(r => {
          const Icon = r.icon;
          const isPdf = r.format === 'pdf';
          return (
            <Card key={r.id} className={`bg-white border rounded-lg p-5 hover:shadow-md transition-shadow ${isPdf ? 'border-red-100 ring-1 ring-red-50' : 'border-slate-200'}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isPdf ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}><Icon size={18} /></div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{r.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{r.desc}</p>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => downloadReport(r.id)} disabled={generating === r.id} className="w-full justify-center" data-testid={`download-${r.id}`}>
                {generating === r.id ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Download size={14} className="mr-1.5" />}
                {generating === r.id ? 'Generating...' : `Download ${isPdf ? 'PDF' : 'Excel'}`}
              </Button>
            </Card>
          );
        })}
      </div>

      <Card className="bg-white border border-slate-200 rounded-lg p-6" data-testid="exec-summary-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 font-heading">AI Executive Narrative</h3>
            <p className="text-xs text-slate-400 mt-0.5">LLM-generated summary based on deterministic calculations</p>
          </div>
          <Button size="sm" onClick={generateExecSummary} disabled={loadingSummary} className="bg-slate-900 hover:bg-slate-800" data-testid="generate-summary-btn">
            {loadingSummary ? <><Loader2 size={14} className="animate-spin mr-1.5" />Generating...</> : 'Generate Summary'}
          </Button>
        </div>
        {execSummary ? (
          <div className="prose prose-sm max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="exec-summary-text">{execSummary}</div>
        ) : (
          <p className="text-sm text-slate-400 text-center py-8">Click "Generate Summary" to create an AI-powered executive narrative based on current data</p>
        )}
      </Card>
    </div>
  );
}
